import { useMemo } from "react";
import type { SpecDataType } from "../../../../common-deprecated/types/SpecTypes";
import type { ExtraType } from "../../../../common-deprecated/types/CommonTypes";
import { useCarConfigSelector } from "../redux/store";
import {
    selectCarConfigSpecsSearchIsSearching,
    selectCarConfigSpecsSearchValue,
} from "../redux/slices/CarConfigSpecsSearchSlice";
import { getCcisPath } from "../../../../common-deprecated/settings/utils/ccisSettingUtils";
import { TechnicalSpecificationCategory } from "../../../../common-deprecated/constants";
import { getCarConfigLabel } from "../utils/labelUtils";
import type { CarConfigLabelType } from "../utils/labels";

export enum SpecsCarDBAssetSlot {
    carConfigSpecsSpaceAndRoom = "specs-section-space-and-room",
    carConfigSpecsEngine = "specs-section-engine",
    carConfigSpecsSafety = "specs-section-safety",
    carConfigSpecsStyling = "specs-section-styling",
    carConfigSpecsPerformance = "specs-section-performance",
    carConfigSpecsWheels = "specs-section-wheels",
    carConfigSpecsComfort = "specs-section-comfort",
    carConfigSpecsMultimedia = "specs-section-multimedia",
    carConfigSpecsStorage = "specs-section-storage",
    carConfigSpecsProtection = "specs-section-protection",
}

const SPEC_MAPPING: SpecMappingType = {
    carConfigSpecsSpaceAndRoom: {
        type: "specs",
        data: ["DIMWGHT", "IntDim", "LOADCAP", "CAPACITIES"],
        descriptionKey: "carConfigSpecsSpaceAndRoomDescription",
        secondaryDescriptionKey: "carConfigSpecsSpaceAndRoomSecondaryDescription",
    },
    carConfigSpecsEngine: {
        type: "specs",
        data: [
            TechnicalSpecificationCategory.EnvironmentalPerformance,
            "PERF",
            "ENG",
            "TRANS",
            "SUSP",
            "BRK",
            "WEIGHTTOW",
            "SYSTEM OUTPUT",
            "ELECTRIC MOTOR",
            "CHARGING",
            TechnicalSpecificationCategory.FuelConsumption,
            TechnicalSpecificationCategory.Emission,
            TechnicalSpecificationCategory.ElectricConsumption,
            TechnicalSpecificationCategory.ElectricRange,
            "NVHPERF",
        ],
        descriptionKey: "carConfigSpecsEngineDescription",
        secondaryDescriptionKey: "carConfigSpecsEngineSecondaryDescription",
    },
    carConfigSpecsSafety: {
        type: "equipment",
        data: [
            "EXT/ExteriorSafety",
            "INT/InteriorSafety",
            "INT/ACTIVE SAFETY & DRIVING DYNAMICS",
            "INT/PASSIVE SAFETY",
        ],
        descriptionKey: "carConfigSpecsSafetyDescription",
        secondaryDescriptionKey: "carConfigSpecsSafetySecondaryDescription",
    },
    carConfigSpecsStyling: {
        type: "equipment",
        data: ["EXT/ExteriorStyling", "INT/InteriorStyling", "EXT/EXTERIOR"],
        descriptionKey: "carConfigSpecsStylingDescription",
        secondaryDescriptionKey: "carConfigSpecsStylingSecondaryDescription",
    },
    carConfigSpecsPerformance: {
        type: "equipment",
        data: ["EXT/ExteriorPerformance"],
        descriptionKey: "carConfigSpecsPerformanceDescription",
        secondaryDescriptionKey: "carConfigSpecsPerformanceSecondaryDescription",
    },
    carConfigSpecsWheels: {
        type: "equipment",
        data: ["EXT/TYRES-WHEELS"],
        descriptionKey: "carConfigSpecsWheelsDescription",
        secondaryDescriptionKey: "carConfigSpecsWheelsSecondaryDescription",
    },
    carConfigSpecsComfort: {
        type: "equipment",
        data: ["EXT/ExteriorComfort", "INT/InteriorComfort", "INT/INTERIOR COMFORT & CONVENIENCE"],
        descriptionKey: "carConfigSpecsComfortDescription",
        secondaryDescriptionKey: "carConfigSpecsComfortSecondaryDescription",
    },
    carConfigSpecsMultimedia: {
        type: "equipment",
        data: ["INT/Multimedia", "INT/AUDIO, COMMUNICATION & INFORMATION"],
        descriptionKey: "carConfigSpecsMultimediaDescription",
        secondaryDescriptionKey: "carConfigSpecsMultimediaSecondaryDescription",
    },
    carConfigSpecsStorage: {
        type: "equipment",
        data: ["EXT/ExteriorTransport"],
        descriptionKey: "carConfigSpecsStorageDescription",
        secondaryDescriptionKey: "carConfigSpecsStorageSecondaryDescription",
    },
    carConfigSpecsProtection: {
        type: "equipment",
        data: ["INT/InteriorProtection", "INT/SECURITY"],
        descriptionKey: "carConfigSpecsProtectionDescription",
        secondaryDescriptionKey: "carConfigSpecsProtectionSecondaryDescription",
    },
};

type SpecTypeType = "specs" | "equipment";
type SpecTranslationType =
    | "carConfigSpecsSpaceAndRoom"
    | "carConfigSpecsEngine"
    | "carConfigSpecsSafety"
    | "carConfigSpecsStyling"
    | "carConfigSpecsPerformance"
    | "carConfigSpecsWheels"
    | "carConfigSpecsComfort"
    | "carConfigSpecsMultimedia"
    | "carConfigSpecsStorage"
    | "carConfigSpecsProtection";

type SpecMappingType = Record<
    SpecTranslationType,
    {
        type: SpecTypeType;
        data: string[];
        descriptionKey: CarConfigLabelType;
        secondaryDescriptionKey: CarConfigLabelType;
    }
>;

export type SpecGroupType = {
    id: string;
    name: string;
    image: string;
    description: string;
    secondaryDescription: string;
    imageLarge: string;
    type: SpecTypeType;
    categories: {
        id: string;
        name: string | null;
        items: SpecDataType[] | ExtraType[];
    }[];
};

export const useSpecs = (filterResults: boolean = false): SpecGroupType[] => {
    const carConfigSettings = useCarConfigSelector((state) => state.carConfigSettings);
    const commonSettings = useCarConfigSelector((state) => state.commonSettings);
    const modelId = useCarConfigSelector((state) => state.car.model.id);
    const specs = useCarConfigSelector((state) => state.specs);
    const searchValue = useCarConfigSelector(selectCarConfigSpecsSearchValue);
    const isSearching = useCarConfigSelector(selectCarConfigSpecsSearchIsSearching);

    return useMemo<SpecGroupType[]>(() => {
        const formattedSpecs = Object.entries(SPEC_MAPPING)
            .map(([name, { type, data, descriptionKey, secondaryDescriptionKey }]) => {
                let categories: SpecGroupType["categories"] = [];
                if (type === "specs") {
                    categories = data
                        .reduce<SpecGroupType["categories"]>((all, specCode) => {
                            const specCategory = specs.techSpecs.find(({ internalCode }) => internalCode === specCode);

                            if (specCategory) {
                                return [
                                    ...all,
                                    {
                                        id: specCategory.id,
                                        name: specCategory.title ?? null,
                                        items:
                                            specCategory.data?.filter(
                                                (item) =>
                                                    !isSearching ||
                                                    !filterResults ||
                                                    (searchValue &&
                                                        item.name
                                                            .toLocaleLowerCase()
                                                            .includes(searchValue.toLocaleLowerCase())),
                                            ) ?? [],
                                    },
                                ];
                            }

                            return all;
                        }, [])
                        .filter((item) => item.items.length !== 0);
                } else if (type === "equipment") {
                    categories = data
                        .reduce<SpecGroupType["categories"]>((all, catCode) => {
                            return [
                                ...all,
                                {
                                    id: catCode,
                                    name: null,
                                    items: specs.standardEquipment
                                        .filter(({ category: { code } }) => code === catCode)
                                        .filter(
                                            (item) =>
                                                !isSearching ||
                                                !filterResults ||
                                                (searchValue &&
                                                    item.name
                                                        .toLocaleLowerCase()
                                                        .includes(searchValue.toLocaleLowerCase())),
                                        ),
                                },
                            ];
                        }, [])
                        .filter((item) => item.items.length !== 0);
                }
                return {
                    id: name,
                    name: getCarConfigLabel({ commonSettings, carConfigSettings }, name as CarConfigLabelType),
                    description: getCarConfigLabel({ commonSettings, carConfigSettings }, descriptionKey),
                    secondaryDescription: getCarConfigLabel(
                        { commonSettings, carConfigSettings },
                        secondaryDescriptionKey,
                    ),
                    image: `${getCcisPath(commonSettings)}/${commonSettings.country}/model/${modelId}/width/640/image-type/${
                        SpecsCarDBAssetSlot[name as SpecTranslationType]
                    }/model.webp`,
                    imageLarge: `${getCcisPath(commonSettings)}/${
                        commonSettings.country
                    }/model/${modelId}/width/1280/image-type/${
                        SpecsCarDBAssetSlot[name as SpecTranslationType]
                    }/model.webp`,
                    type,
                    categories,
                };
            })
            .filter((item) => item.categories.length !== 0);

        return formattedSpecs;
    }, [specs, commonSettings, filterResults, searchValue, isSearching]);
};
